import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  PRODUCT_TYPES,
  type AdminFeedbackResponse,
  type DashboardSummary,
} from '@fois-utsavvoice/shared';
import { downloadResponsesCsv, getDashboardSummary, listResponses } from '../api/admin.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { PageShell } from '../components/PageShell.js';

const emptySummary: DashboardSummary = {
  totalRequests: 0,
  totalResponses: 0,
  completionRate: 0,
  averageOverallRating: null,
  averagePackagingRating: null,
  averageProductRating: null,
  averageValueRating: null,
  recommendYesPercent: null,
  purchaseAgainYesPercent: null,
};

export function AdminDashboardPage() {
  const auth = useAuth();
  const [summary, setSummary] = useState<DashboardSummary>(emptySummary);
  const [items, setItems] = useState<AdminFeedbackResponse[]>([]);
  const [error, setError] = useState<string | undefined>();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [productType, setProductType] = useState('');
  const [rating, setRating] = useState('');
  const [recommendation, setRecommendation] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [testimonialEligible, setTestimonialEligible] = useState('');

  const filters = useMemo(
    () => ({
      from: from ? new Date(from).toISOString() : undefined,
      to: to ? new Date(`${to}T23:59:59.000Z`).toISOString() : undefined,
      productType: productType || undefined,
      rating: rating || undefined,
      recommendation: recommendation || undefined,
      reviewStatus: reviewStatus || undefined,
      testimonialEligible: testimonialEligible || undefined,
    }),
    [from, to, productType, rating, recommendation, reviewStatus, testimonialEligible],
  );

  useEffect(() => {
    if (!auth.tokens) {
      return;
    }
    Promise.all([
      getDashboardSummary(auth.tokens.idToken),
      listResponses(auth.tokens.idToken, filters),
    ])
      .then(([nextSummary, page]) => {
        setSummary(nextSummary);
        setItems(page.items);
        setError(undefined);
      })
      .catch(() => setError('Dashboard data could not be loaded.'));
  }, [auth.tokens, filters]);

  return (
    <PageShell>
      <AdminNav />
      <section className="card">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="heading-xl text-crimson">Dashboard</h1>
          <button
            className="btn-secondary"
            type="button"
            onClick={() => auth.tokens && downloadResponsesCsv(auth.tokens.idToken, filters)}
          >
            Export CSV
          </button>
        </div>
        <dl className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Stat label="Total requests" value={summary.totalRequests} />
          <Stat label="Total responses" value={summary.totalResponses} />
          <Stat label="Completion rate" value={`${summary.completionRate}%`} />
          <Stat label="Avg overall" value={summary.averageOverallRating ?? '—'} />
          <Stat label="Avg packaging" value={summary.averagePackagingRating ?? '—'} />
          <Stat label="Avg product selection" value={summary.averageProductRating ?? '—'} />
          <Stat label="Avg value" value={summary.averageValueRating ?? '—'} />
          <Stat label="Recommend yes" value={percent(summary.recommendYesPercent)} />
          <Stat label="Buy again yes" value={percent(summary.purchaseAgainYesPercent)} />
        </dl>
      </section>

      <section className="card mt-6">
        <h2 className="heading-lg text-crimson">Responses</h2>
        <form className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="From" id="from">
            <input
              id="from"
              type="date"
              className="field-input"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </Field>
          <Field label="To" id="to">
            <input
              id="to"
              type="date"
              className="field-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </Field>
          <Field label="Box type" id="productType">
            <select
              id="productType"
              className="field-input"
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
            >
              <option value="">All</option>
              {PRODUCT_TYPES.map((item) => (
                <option key={item.code} value={item.code}>
                  {item.displayName}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Overall rating" id="rating">
            <select
              id="rating"
              className="field-input"
              value={rating}
              onChange={(e) => setRating(e.target.value)}
            >
              <option value="">All</option>
              {[5, 4, 3, 2, 1].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Recommendation" id="recommendation">
            <select
              id="recommendation"
              className="field-input"
              value={recommendation}
              onChange={(e) => setRecommendation(e.target.value)}
            >
              <option value="">All</option>
              <option value="YES">Yes</option>
              <option value="MAYBE">Maybe</option>
              <option value="NO">No</option>
            </select>
          </Field>
          <Field label="Review status" id="reviewStatus">
            <select
              id="reviewStatus"
              className="field-input"
              value={reviewStatus}
              onChange={(e) => setReviewStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="NEW">New</option>
              <option value="REVIEWED">Reviewed</option>
              <option value="ARCHIVED">Archived</option>
            </select>
          </Field>
          <Field label="Testimonial eligible" id="testimonialEligible">
            <select
              id="testimonialEligible"
              className="field-input"
              value={testimonialEligible}
              onChange={(e) => setTestimonialEligible(e.target.value)}
            >
              <option value="">All</option>
              <option value="true">Eligible</option>
              <option value="false">Private only</option>
            </select>
          </Field>
        </form>
        {error ? (
          <p className="mt-4 text-sm font-semibold text-crimson" role="alert">
            {error}
          </p>
        ) : null}
        <div className="mt-6 overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead>
              <tr className="border-b border-linen">
                <th className="py-2 pr-4">Submitted</th>
                <th className="py-2 pr-4">Product</th>
                <th className="py-2 pr-4">Overall</th>
                <th className="py-2 pr-4">Recommend</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2"> </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.responseId} className="border-b border-linen">
                  <td className="py-3 pr-4">{new Date(item.submittedAt).toLocaleDateString()}</td>
                  <td className="py-3 pr-4">{item.productName}</td>
                  <td className="py-3 pr-4">{item.overallRating}</td>
                  <td className="py-3 pr-4">{item.recommendation}</td>
                  <td className="py-3 pr-4">{item.reviewStatus}</td>
                  <td className="py-3">
                    <Link
                      className="font-semibold text-crimson underline"
                      to={`/admin/responses/${item.responseId}`}
                    >
                      Review
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && !error ? (
            <p className="mt-4 text-charcoal/80">No responses match these filters.</p>
          ) : null}
        </div>
      </section>
    </PageShell>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-none border border-linen bg-cream px-4 py-3">
      <dt className="text-sm font-semibold text-charcoal/70">{label}</dt>
      <dd className="mt-1 text-lg font-semibold text-charcoal">{value}</dd>
    </div>
  );
}

function Field({ label, id, children }: { label: string; id: string; children: ReactNode }) {
  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      {children}
    </div>
  );
}

function percent(value: number | null): string {
  return value === null ? '—' : `${value}%`;
}
